import React, { useState, useEffect } from 'react';
import { X, Save, User, Mail, Phone, Building, Briefcase, Award, CheckCircle } from 'lucide-react';
import { adminService } from '../../services/adminService.js';
import { toast } from 'react-hot-toast';

const EditUserModal = ({
  isOpen,
  onClose,
  onSuccess,
  user,
  userType
}) => {
  const [formData, setFormData] = useState({});
  const [skillInput, setSkillInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('none');
  const [selectedDuration, setSelectedDuration] = useState(1);

  const normalizePlan = (plan) => {
    if (!plan) return 'none';
    const normalized = plan.toLowerCase();
    if (normalized === 'core' || normalized === 'nexora' || normalized === 'enterprise') {
      return normalized;
    }
    return 'none';
  };

  const parseDateValue = (value) => {
    if (!value) return null;
    if (value.toDate) {
      return value.toDate();
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const deriveDurationFromUser = (userData) => {
    if (!userData) return 1;
    if (userData.planDuration) {
      const duration = Number(userData.planDuration);
      if (!isNaN(duration)) {
        return duration;
      }
    }

    const start = parseDateValue(userData.subscription?.startDate || userData.startDate);
    const end = parseDateValue(userData.subscription?.expiresAt || userData.endDate || userData.subscriptionEndsAt);

    if (start && end) {
      const diffInMonths = Math.max(1, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30)));
      if (diffInMonths <= 2) return 1;
      if (diffInMonths <= 4) return 3;
      if (diffInMonths <= 8) return 6;
      return 12;
    }

    return 'unlimited';
  };

  const addMonths = (date, months) => {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
  };

  const formatDate = (date) => {
    if (!date) return 'No expiry set';
    return date.toLocaleDateString();
  };

  useEffect(() => {
    if (user) {
      if (userType === 'jobseeker') {
        setFormData({
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          skills: user.skills || [],
          experience: user.experience || '',
          education: user.education || '',
          skillsVerified: user.skillsVerified || false
        });
      } else {
        setFormData({
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          company: user.company || '',
          designation: user.designation || ''
        });

        const plan = normalizePlan(user.subscription?.plan || user.subscriptionPlan);
        setSelectedPlan(plan);
        setSelectedDuration(deriveDurationFromUser(user));
      }
    } else {
      setSelectedPlan('none');
      setSelectedDuration(1);
    }
  }, [user, userType]);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let payload = { ...formData };

      if (userType === 'hr') {
        const now = new Date();
        const hasPlan = selectedPlan !== 'none';
        const expiryDate =
          hasPlan && selectedDuration !== 'unlimited'
            ? addMonths(now, Number(selectedDuration))
            : null;

        payload = {
          ...payload,
          subscription: {
            plan: selectedPlan,
            status: hasPlan ? 'active' : 'inactive',
            startDate: hasPlan ? now : null,
            expiresAt: hasPlan ? expiryDate : null
          },
          subscriptionPlan: hasPlan ? selectedPlan.toUpperCase() : 'none',
          planDuration: hasPlan && selectedDuration !== 'unlimited' ? Number(selectedDuration) : null,
          startDate: hasPlan ? now.toISOString() : null,
          endDate: hasPlan && expiryDate ? expiryDate.toISOString() : null,
          isActive: hasPlan,
          isPremium: hasPlan,
          subscriptionStatus: hasPlan ? 'active' : 'inactive',
          subscriptionEndsAt: hasPlan ? expiryDate : null
        };
      }

      const result = userType === 'jobseeker'
        ? await adminService.updateJobSeeker(user.id, payload)
        : await adminService.updateHRUser(user.id, payload);

      if (result.success) {
        toast.success(`${userType === 'jobseeker' ? 'Job seeker' : 'HR user'} updated successfully!`);
        onSuccess();
        onClose();
      } else {
        toast.error(result.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.message || 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddSkill = () => {
    if (skillInput.trim() && !formData.skills?.includes(skillInput.trim())) {
      setFormData({
        ...formData,
        skills: [...(formData.skills || []), skillInput.trim()]
      });
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skill) => {
    setFormData({
      ...formData,
      skills: formData.skills?.filter(s => s !== skill) || []
    });
  };

  const currentPlan = normalizePlan(user.subscription?.plan || user.subscriptionPlan);
  const currentStatus = (user.subscription?.status || (user.isActive ? 'active' : 'inactive'));
  const currentExpiry = parseDateValue(user.subscription?.expiresAt || user.endDate || user.subscriptionEndsAt);
  const previewExpiry =
    selectedPlan === 'none'
      ? null
      : selectedDuration === 'unlimited'
        ? null
        : addMonths(new Date(), Number(selectedDuration));
  const selectedPlanLabel = selectedPlan === 'none' ? 'No Plan' : selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1);
  const planSummary =
    selectedPlan === 'none'
      ? 'Saving will set no active plan.'
      : `Saving will set ${selectedPlanLabel} plan ${selectedDuration === 'unlimited' ? 'with no expiry.' : `until ${formatDate(previewExpiry)}.`}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 ${userType === 'jobseeker' ? 'bg-green-100' : 'bg-blue-100'} rounded-lg flex items-center justify-center`}>
              <User className={`w-5 h-5 ${userType === 'jobseeker' ? 'text-green-600' : 'text-blue-600'}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Edit {userType === 'jobseeker' ? 'Job Seeker' : 'HR User'}
              </h2>
              <p className="text-sm text-gray-500">{user.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address (Read-only)
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={formData.email || ''}
                  disabled
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Email cannot be changed after creation</p>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* HR-specific fields */}
          {userType === 'hr' && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Company Information</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.company || ''}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Designation
                </label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.designation || ''}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="pt-2 border-t border-gray-200 space-y-3">
                <h4 className="text-md font-semibold text-gray-900 flex items-center">
                  <Award className="w-4 h-4 mr-2 text-blue-600" />
                  Subscription
                </h4>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subscription Plan
                    </label>
                    <select
                      value={selectedPlan}
                      onChange={(e) => setSelectedPlan(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="none">No Plan (inactive)</option>
                      <option value="core">Core</option>
                      <option value="nexora">Nexora</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration
                    </label>
                    <select
                      value={String(selectedDuration)}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedDuration(value === 'unlimited' ? 'unlimited' : Number(value));
                      }}
                      disabled={selectedPlan === 'none'}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-50"
                    >
                      <option value="1">1 Month</option>
                      <option value="3">3 Months</option>
                      <option value="6">6 Months</option>
                      <option value="12">12 Months</option>
                      <option value="unlimited">No Expiry</option>
                    </select>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700 space-y-1">
                  <p className="font-medium text-gray-900">Current subscription</p>
                  <p>
                    Plan: {currentPlan === 'none' ? 'None' : currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} ({currentStatus || 'inactive'})
                  </p>
                  <p>Expires: {formatDate(currentExpiry)}</p>
                  <p className="text-gray-600">{planSummary}</p>
                </div>
              </div>
            </div>
          )}

          {/* Job Seeker-specific fields */}
          {userType === 'jobseeker' && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Professional Information</h3>

              {/* Skills Verification Toggle */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.skillsVerified || false}
                    onChange={(e) => setFormData({ ...formData, skillsVerified: e.target.checked })}
                    className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm font-medium text-gray-900">Skills Verified (Manual Override)</span>
                  </div>
                </label>
                <p className="mt-1 text-xs text-gray-600 ml-8">
                  Check this to manually verify the candidate's skills without them taking tests
                </p>
              </div>

              {/* Skills */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Skills
                </label>
                <div className="flex space-x-2 mb-2">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSkill();
                      }
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Add a skill"
                  />
                  <button
                    type="button"
                    onClick={handleAddSkill}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
                {formData.skills && formData.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.skills.map((skill) => (
                      <span
                        key={skill}
                        className="inline-flex items-center px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm"
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => handleRemoveSkill(skill)}
                          className="ml-2 text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Experience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Years of Experience
                </label>
                <select
                  value={formData.experience || ''}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">Select experience level</option>
                  <option value="0-1">0-1 years</option>
                  <option value="1-3">1-3 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="5-10">5-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>

              {/* Education */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Highest Education
                </label>
                <select
                  value={formData.education || ''}
                  onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">Select education level</option>
                  <option value="High School">High School</option>
                  <option value="Associate Degree">Associate Degree</option>
                  <option value="Bachelor's Degree">Bachelor's Degree</option>
                  <option value="Master's Degree">Master's Degree</option>
                  <option value="Ph.D.">Ph.D.</option>
                </select>
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;

